import React, { useState } from 'react';
import { AlertTriangle, Calendar, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import CommonModal from '../../ui/CommonModal';
import { LoadingSpinner } from '../../ui/Loading';
import { useFormatDate } from '../../../hooks/useFormatDate';
import {
  useClearPlayerAutoRefundWarning,
  usePlayerAutoRefundSummary,
} from '../../../hooks/useApi';
import { cn, formatCurrency } from '../../../utils';
import type { PlayerAutoRefundSummary } from '../../../types';

interface AutoRefundWarningCardProps {
  userId: string;
  canClearWarning: boolean;
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function ClearAutoRefundWarningModal({
  userId,
  isOpen,
  onClose,
  onSuccess,
}: {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [adminNotes, setAdminNotes] = useState('');
  const clearMutation = useClearPlayerAutoRefundWarning();

  const handleClose = () => {
    if (clearMutation.isLoading) return;
    setAdminNotes('');
    onClose();
  };

  const handleConfirm = async () => {
    await clearMutation.mutateAsync({
      userId,
      data: adminNotes.trim() ? { adminNotes: adminNotes.trim() } : undefined,
    });
    setAdminNotes('');
    onSuccess();
    onClose();
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Clear auto-refund warning"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={clearMutation.isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={clearMutation.isLoading}>
            {clearMutation.isLoading ? 'Clearing…' : 'Clear warning'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          This resets the player&apos;s auto-refund counters and removes the external withdrawal block.
          Use this after reviewing the player&apos;s ambiguous deposit refunds.
        </p>
        <div>
          <label htmlFor="autoRefundAdminNotes" className="block text-sm font-medium mb-2">
            Admin notes (optional)
          </label>
          <textarea
            id="autoRefundAdminNotes"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            rows={3}
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Reason for clearing the warning…"
          />
        </div>
      </div>
    </CommonModal>
  );
}

function AutoRefundSummaryBody({
  summary,
  fmt,
}: {
  summary: PlayerAutoRefundSummary;
  fmt: (value: string | null | undefined) => string;
}) {
  const warningActive = summary.autoRefundWarning;
  const hasHistory = summary.autoRefundCount > 0 || summary.autoRefundAmount > 0;

  return (
    <div className="space-y-3">
      {warningActive ? (
        <p
          className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2"
          role="status"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <span>
            External withdrawals are blocked for this player because cumulative auto-refunds exceeded the
            warning threshold.
          </span>
        </p>
      ) : null}

      <SummaryRow label="Auto-refund count" value={summary.autoRefundCount} />
      <SummaryRow
        label="Auto-refund amount"
        value={formatCurrency(summary.autoRefundAmount)}
      />
      <SummaryRow
        label="Warning threshold"
        value={formatCurrency(summary.warningThreshold)}
      />
      <SummaryRow
        label="Warning status"
        value={
          <span
            className={cn(
              'inline-flex rounded-md px-2 py-0.5 text-xs font-medium',
              warningActive ? 'bg-amber-500/10 text-amber-900' : 'bg-muted text-muted-foreground'
            )}
          >
            {warningActive ? 'Active — withdrawals blocked' : 'None'}
          </span>
        }
      />

      {summary.autoRefundWarningAt ? (
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Warning since</span>
          <span className="flex items-center">
            <Calendar className="h-3 w-3 mr-1" aria-hidden />
            {fmt(summary.autoRefundWarningAt)}
          </span>
        </div>
      ) : null}

      {summary.autoRefundLastResetAt ? (
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Last cleared</span>
          <span className="flex items-center">
            <Calendar className="h-3 w-3 mr-1" aria-hidden />
            {fmt(summary.autoRefundLastResetAt)}
          </span>
        </div>
      ) : null}

      {!warningActive && !hasHistory ? (
        <p className="text-sm text-muted-foreground">
          No ambiguous deposit auto-refunds recorded for this player.
        </p>
      ) : null}
    </div>
  );
}

const AutoRefundWarningCard: React.FC<AutoRefundWarningCardProps> = ({ userId, canClearWarning }) => {
  const fmt = useFormatDate();
  const { data, isLoading, error, refetch } = usePlayerAutoRefundSummary(userId);
  const [showClearModal, setShowClearModal] = useState(false);

  const summary = data?.data;
  const warningActive = summary?.autoRefundWarning === true;

  return (
    <>
      <Card
        className={cn(
          warningActive ? 'border-amber-200 bg-amber-50/40' : undefined
        )}
      >
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className={cn('h-5 w-5 shrink-0', warningActive ? 'text-amber-700' : 'text-muted-foreground')} />
            Auto-refund warning
          </CardTitle>
          {canClearWarning && warningActive ? (
            <Button variant="outline" size="sm" onClick={() => setShowClearModal(true)}>
              <RotateCcw className="h-4 w-4 mr-1" />
              Clear warning
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-6">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="space-y-2 text-sm">
              <p className="text-destructive">Failed to load auto-refund status.</p>
              <button type="button" onClick={() => refetch()} className="text-primary hover:underline text-xs">
                Try again
              </button>
            </div>
          ) : summary ? (
            <AutoRefundSummaryBody summary={summary} fmt={fmt} />
          ) : null}
        </CardContent>
      </Card>

      {canClearWarning ? (
        <ClearAutoRefundWarningModal
          userId={userId}
          isOpen={showClearModal}
          onClose={() => setShowClearModal(false)}
          onSuccess={() => refetch()}
        />
      ) : null}
    </>
  );
};

export default AutoRefundWarningCard;
